import { useId } from 'react';

import { TeamAvatar } from '../Avatar';
import { cn } from '../cn';
import { usePlatformLink } from '../contexts';
import Button from '../Input2/Button';
import FilterPopover from '../Input2/FilterPopover';
import { Arrows } from '../StrokeIcons';

export type TeamBreadcrumbSwitcherTeam = {
  teamId: string;
  teamName: string;
  teamLogo: { url?: string; webp?: string } | null;
};

export type TeamBreadcrumbSwitcherProps = {
  currentTeam: TeamBreadcrumbSwitcherTeam;
  teams: TeamBreadcrumbSwitcherTeam[];
  onTeamChange: (teamId: string) => void;
  className?: string;
};

export default function TeamBreadcrumbSwitcher({
  currentTeam,
  teams,
  onTeamChange,
  className,
}: TeamBreadcrumbSwitcherProps) {
  const Link = usePlatformLink();
  const id = useId();
  const popoverId = `team-switcher-${id.replace(/:/g, '')}`;
  const anchorName = `--team-switcher-anchor-${id.replace(/:/g, '')}`;

  const hasMultipleTeams = teams.length > 1;

  const teamItems = teams.map((t) => ({
    value: t.teamId,
    label: t.teamName,
    icon: <TeamAvatar logo={t.teamLogo} name={t.teamName} className="size-4" shape="circle" />,
  }));

  return (
    <div
      className={cn(
        'flex flex-row items-center gap-1 text-nowrap text-sm [&>svg]:h-4 [&>svg]:w-4',
        'text-[#475166] dark:text-white',
        className,
      )}>
      <Link href="/teams/active" className="flex items-center gap-2 hover:underline">
        <TeamAvatar logo={currentTeam.teamLogo} name={currentTeam.teamName} className="size-4" shape="circle" />
        {currentTeam.teamName}
      </Link>
      {hasMultipleTeams && (
        <>
          <Button
            size="xs"
            text
            icon={<Arrows.ChevronDown className="size-3" />}
            tooltip="Switch team"
            popoverTarget={popoverId}
            style={{ anchorName } as React.CSSProperties}
          />
          <FilterPopover
            popoverId={popoverId}
            anchorName={anchorName}
            size="sm"
            items={teamItems}
            selectedValue={currentTeam.teamId}
            onSelect={onTeamChange}
            filterable={teams.length > 5}
            popoverStyle={{ minWidth: 200 }}
          />
        </>
      )}
    </div>
  );
}
